import React, { useState } from "react";
import { motion } from "framer-motion";
import { QRCodeSVG } from "qrcode.react";
import { RotateCw } from "lucide-react";
import { Compass, PlaneIcon, Crest, Chip, WorldMap, WorldMapArt, HeartIcon, Stamp } from "./Deco";
import { WEDDING } from "../content";

const W = 360;
const H = 514;

export const PassportCover = ({ t, opened, onOpen, onFinished }) => {
  const startFlipped =
    typeof window !== "undefined" && new URLSearchParams(window.location.search).has("flip");
  const [flipped, setFlipped] = useState(startFlipped);
  const [showBack, setShowBack] = useState(startFlipped);

  const doOpen = () => {
    setFlipped(false);
    onOpen();
  };

  return (
    <motion.div
      className="fixed inset-0 z-40 flex flex-col items-center justify-center leather px-4 py-6"
      initial={{ opacity: 1 }}
      animate={{ opacity: opened ? 0 : 1 }}
      transition={{ duration: 0.7, ease: "easeInOut", delay: opened ? 0.35 : 0 }}
      onAnimationComplete={() => opened && onFinished && onFinished()}
      style={{ pointerEvents: opened ? "none" : "auto" }}
    >
      <div
        className="absolute inset-0 opacity-70"
        style={{ background: "radial-gradient(circle at 50% 40%, rgba(70,105,70,0.35), transparent 62%)" }}
      />

      <div className="relative flex flex-col items-center">
        {/* scale wrapper keeps the passport perfectly proportional on every screen */}
        <div className="origin-center scale-[0.7] sm:scale-100">
          <div className="perspective">
            <div className="preserve-3d relative" style={{ width: W, height: H }}>
              {/* inner page revealed on open (only while opening, so it never shows during flip) */}
              {opened && <InnerPage />}

              {/* open-swing around the left spine */}
              <motion.div
                className="preserve-3d absolute inset-0"
                initial={{ rotateY: 0 }}
                animate={{ rotateY: opened ? -158 : 0 }}
                transition={{ duration: 1.0, ease: [0.76, 0, 0.24, 1] }}
                style={{ transformOrigin: "left center" }}
              >
                {/* flip container — renders ONE face at a time (swaps at mid-turn)
                    so the two faces never show through each other on iOS Safari */}
                <motion.div
                  className="preserve-3d absolute inset-0"
                  animate={{ rotateY: flipped ? 180 : 0 }}
                  transition={{ duration: 0.9, ease: [0.76, 0, 0.24, 1] }}
                  onUpdate={(latest) => {
                    const raw =
                      typeof latest.rotateY === "number"
                        ? latest.rotateY
                        : parseFloat(latest.rotateY) || 0;
                    const deg = ((raw % 360) + 360) % 360;
                    const back = deg > 90 && deg < 270;
                    setShowBack((prev) => (prev === back ? prev : back));
                  }}
                >
                  {showBack ? (
                    <div
                      className="absolute inset-0 cursor-pointer"
                      style={{ transform: "rotateY(180deg)" }}
                      onClick={() => setFlipped(false)}
                      data-testid="passport-back"
                    >
                      <BackCover />
                    </div>
                  ) : (
                    <div
                      className="absolute inset-0 cursor-pointer"
                      data-testid="passport-cover-clickable"
                      onClick={!opened ? doOpen : undefined}
                    >
                      <FrontCover t={t} />
                    </div>
                  )}
                </motion.div>

                {/* darken while turning */}
                <motion.div
                  className="absolute inset-0 rounded-r-2xl rounded-l-md bg-black pointer-events-none backface-hidden"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: opened ? 0.45 : 0 }}
                  transition={{ duration: 1.2 }}
                />
              </motion.div>
            </div>
          </div>
        </div>

        {/* controls */}
        {!opened && (
          <motion.div
            className="relative z-10 mt-7 sm:mt-9 flex flex-col items-center gap-3"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.1, duration: 0.8 }}
          >
            <button
              data-testid="open-passport-btn"
              onClick={doOpen}
              className="group relative px-9 py-3.5 rounded-full font-sans tracking-[0.22em] text-xs uppercase text-[var(--olive)] bg-gradient-to-r from-[#BF953F] via-[#FCF6BA] to-[#B38728] transition-transform duration-300 hover:scale-105 active:scale-95 shadow-[0_12px_34px_rgba(212,175,55,0.4)] gleam"
            >
              {t.open}
            </button>
            <button
              data-testid="flip-passport-btn"
              onClick={() => setFlipped((f) => !f)}
              className="flex items-center gap-2 text-[var(--gold)]/80 text-[11px] tracking-[0.2em] uppercase hover:text-[var(--gold)] transition-colors"
            >
              <RotateCw size={13} /> {t.flip}
            </button>
          </motion.div>
        )}
      </div>
    </motion.div>
  );
};

/* ---------- FRONT COVER ---------- */
const FrontCover = ({ t }) => (
  <div className="backface-hidden leather relative w-full h-full rounded-r-2xl rounded-l-md overflow-hidden shadow-[26px_34px_90px_rgba(0,0,0,0.65)] flex flex-col items-center text-center">
    <Spine />
    <Frames />
    <div className="relative z-10 flex flex-col items-center px-7 pt-6 pb-2 flex-1 w-full">
      <div className="gleam gold-glow rounded-full">
        <Crest size={58} left="B" right="F" />
      </div>
      <p className="gold-text font-serif tracking-[0.4em] text-[11px] mt-1">WEDDING</p>
      <h1 className="gold-text gold-glow font-serif font-bold text-[2.5rem] leading-none tracking-[0.1em]">
        {t.title}
      </h1>
      <p className="gold-text font-sans tracking-[0.24em] text-[8px] mt-1.5">
        DÜĞÜN DAVETİYESİ · WEDDING INVITATION
      </p>

      <div className="relative flex-1 flex items-center justify-center my-0.5">
        <div className="gold-glow">
          <Compass size={118} className="opacity-95" />
        </div>
        <PlaneIcon
          size={38}
          className="absolute text-[var(--gold)] gold-glow"
          style={{ transform: "rotate(35deg) translate(10px, 10px)" }}
        />
      </div>

      <h2 className="gold-text font-serif text-[1.35rem] leading-tight px-1">
        {WEDDING.bride}
        <span className="block text-base my-0.5">&amp;</span>
        {WEDDING.groom}
      </h2>

      <div className="flex items-center gap-2 my-1.5 text-[var(--gold)]/70">
        <span className="h-px w-8 bg-current" />
        <HeartIcon size={10} className="text-[var(--gold)]" />
        <span className="h-px w-8 bg-current" />
      </div>

      <p className="gold-text font-mono tracking-[0.2em] text-[11px]">15 NOVEMBER 2026</p>
      <p className="gold-text font-mono tracking-[0.28em] text-[8px] mt-0.5 opacity-90">İSTANBUL · TÜRKİYE</p>
    </div>

    <div className="relative z-10 w-full px-10 pb-6 flex items-center justify-between text-[var(--gold)]/80">
      <div className="flex items-center gap-1.5">
        <Chip size={20} />
        <span className="font-mono text-[7px] tracking-[0.2em]">BIOMETRIC</span>
      </div>
      <a
        href={WEDDING.mapsUrl}
        target="_blank"
        rel="noreferrer"
        data-testid="cover-qr"
        onClick={(e) => e.stopPropagation()}
        className="p-1 rounded bg-[var(--cream)] border border-[var(--gold)]/50"
      >
        <QRCodeSVG value={WEDDING.mapsUrl} size={34} bgColor="#F5ECD8" fgColor="#14241A" level="M" />
      </a>
    </div>
  </div>
);

/* ---------- BACK COVER ---------- */
const BackCover = () => (
  <div className="backface-hidden leather relative w-full h-full rounded-l-2xl rounded-r-md overflow-hidden shadow-[26px_34px_90px_rgba(0,0,0,0.65)] flex flex-col items-center text-center">
    <SpineRight />
    <Frames right />
    <div className="relative z-10 flex flex-col items-center px-8 pt-7 pb-4 flex-1 w-full">
      <div className="gold-glow">
        <Crest size={58} left="B" right="F" />
      </div>
      <p className="gold-text font-serif tracking-[0.16em] text-[14px] mt-1.5 leading-tight">
        MUHABBETLE BAŞLAYAN BİR ÖMÜR
      </p>
      <p className="gold-text font-sans tracking-[0.22em] text-[8px] mt-1">A LIFETIME BEGUN WITH LOVE</p>

      <div className="relative w-full my-2">
        <WorldMapArt className="w-full h-[128px]" color="var(--gold)" />
      </div>

      <div className="flex items-center justify-center gap-5">
        <div className="p-1 rounded bg-[var(--cream)] border border-[var(--gold)]/50">
          <QRCodeSVG value={WEDDING.mapsUrl} size={34} bgColor="#F5ECD8" fgColor="#14241A" level="M" />
        </div>
        <div className="scale-[0.66] text-[var(--gold)] -mx-3">
          <Stamp />
        </div>
      </div>

      <p className="gold-text font-serif italic text-[11px] mt-2 leading-snug max-w-[240px]">
        Bu davetiye sizi özel yolculuğumuza götürür.
      </p>
      <p className="gold-text font-serif text-[9px] opacity-80 mt-0.5 max-w-[240px]">
        This invitation will take you to our special journey.
      </p>
    </div>
  </div>
);

/* ---------- shared bits ---------- */
const Spine = () => (
  <>
    <div className="absolute left-0 top-0 bottom-0 w-[14px] bg-[var(--olive)] z-20" />
    <div className="absolute left-[14px] top-0 bottom-0 w-px bg-[var(--gold)]/25 z-20" />
    <div className="absolute left-[5px] top-8 bottom-8 w-[3px] stitch opacity-45 z-20 rounded-full" />
  </>
);

const SpineRight = () => (
  <>
    <div className="absolute right-0 top-0 bottom-0 w-[14px] bg-[var(--olive)] z-20" />
    <div className="absolute right-[14px] top-0 bottom-0 w-px bg-[var(--gold)]/25 z-20" />
    <div className="absolute right-[5px] top-8 bottom-8 w-[3px] stitch opacity-45 z-20 rounded-full" />
  </>
);

const Frames = ({ right = false }) => (
  <>
    <div
      className={`absolute top-3 bottom-3 ${right ? "left-4 right-6" : "left-6 right-4"} border border-[var(--gold)]/35 rounded-md pointer-events-none z-10`}
    />
    <div
      className={`absolute top-[15px] bottom-[15px] ${right ? "left-[22px] right-[30px]" : "left-[30px] right-[22px]"} border border-[var(--gold)]/15 rounded-md pointer-events-none z-10`}
    />
  </>
);

/* ---------- INNER PAGE (behind the cover) ---------- */
const InnerPage = () => (
  <div
    className="absolute inset-0 paper rounded-r-2xl rounded-l-md overflow-hidden shadow-inner flex flex-col items-center text-center px-7 py-9"
    style={{ transform: "translateZ(-10px)", transformStyle: "preserve-3d" }}
  >
    <div
      className="absolute left-0 top-0 bottom-0 w-10 pointer-events-none"
      style={{ background: "linear-gradient(90deg, rgba(0,0,0,0.22), transparent)" }}
    />
    <div className="absolute inset-3 rounded-r-xl rounded-l-md border border-[var(--gold-dark)]/25 pointer-events-none" />
    <div className="absolute inset-0 opacity-[0.14] pointer-events-none flex items-center">
      <WorldMap className="w-full h-1/2" color="#8a6d2f" />
    </div>

    <div className="relative z-10 gold-glow">
      <Crest size={92} left="B" right="F" color="var(--gold-dark)" />
    </div>
    <p className="relative z-10 font-serif text-2xl text-[var(--forest)] mt-2 leading-tight">Muhabbetle Başlayan Bir Ömür</p>
    <p className="relative z-10 font-sans tracking-[0.18em] text-[10px] text-[var(--gold-dark)] mt-1">A LIFETIME BEGUN WITH LOVE</p>
    <div className="relative z-10 flex-1 flex items-center">
      <p className="font-script text-3xl text-[var(--forest)]/80">Büşra &amp; Furkan</p>
    </div>
    <p className="relative z-10 font-serif italic text-sm text-[var(--olive)]/70 max-w-[240px]">
      Bu davetiye sizi özel yolculuğumuza götürür.
    </p>
    <p className="relative z-10 font-mono text-[9px] tracking-[0.25em] text-[var(--gold-dark)] mt-3">15 · 11 · 2026</p>
  </div>
);
